import { useEffect, useState } from 'react'
import { PageLayout, SideBarNav, HeaderNavigation, Button, Icon } from '@jisr-hr/ds'
import { useHistory, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'utils/hooks'
import { currentEmployeeChangeLanguage, logout } from 'redux/authUser/actionCreators'
import I18n from 'translations/i18n'
import { getTokenFromDB } from 'firebaseUtils'
import { unsubscribeFirebase } from 'redux/actions/firebaseActions'
import { labels } from 'utils/whitelabel'
import Announcement from 'components/global/templates/Announcement/Announcement'
import { toggleNotificationsPanel } from 'redux/appNotifications/actionCreators'
import UserSnap from 'components/UserSnap/UserSnap'
import { isEmpty } from 'lodash'
import { listItems } from './helper'
import { MappingNavigation } from './MappingNavigation'
import ChangePasswordModal from './ChangePasswordModal'
import HelpCenter from './HelpCenter'
import useMainNavigation from './useMainNavigation'

type NewNavigationProps = {
  children: React.ReactNode
}

const NewNavigation = ({ children }: NewNavigationProps): JSX.Element => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { pathname } = useLocation()

  const { initializeAppCalls } = useMainNavigation()

  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false)
  const [sideBarCollapsed, setSideBarCollapsed] = useState(false)
  const [collapseDisabled, setCollapseDisabled] = useState(false)

  const { employee } = useSelector(({ auth }) => auth)
  const list = MappingNavigation()
  const canPostAnnouncement = employee?.permission_details.post_announcements

  const toggleSidebar = (): void => {
    sessionStorage.setItem('collaps', `${!sideBarCollapsed}`)
    setSideBarCollapsed(!sideBarCollapsed)
  }

  const handleLogout = async (): Promise<void> => {
    const unsubscribe = new Promise(() =>
      getTokenFromDB({ clear: true }).then((token) => {
        if (token) return dispatch(unsubscribeFirebase({ token }))
        return null
      }),
    )
    const logoutSession = new Promise(() => dispatch(logout()))
    Promise.all([unsubscribe, logoutSession]).finally(() => history.push('/'))
  }

  const handleHeaderNavigation = (
    e: React.MouseEvent<HTMLLIElement, MouseEvent>,
    data: { value: string },
  ): void => {
    const actions = {
      view_profile: () => history.push(`/employees/${employee?.id}`),
      change_password: () => setIsPasswordModalOpen(true),
      language: (): void => {
        dispatch(
          currentEmployeeChangeLanguage(employee?.id as number, {
            value: I18n.language === 'ar' ? 'en' : 'ar',
          }),
        )
      },
      logout: () => handleLogout(),
    }
    const actionHandler = actions[data.value]
    if (actionHandler) {
      actionHandler()
    }
  }

  const handleCollapse = (): void => {
    sessionStorage.setItem('collaps', `${false}`)
    setSideBarCollapsed(false)
    if (sideBarCollapsed && collapseDisabled) {
      setCollapseDisabled(false)
    }
  }

  useEffect(() => {
    const path = pathname.split('/')[1]
    const currentPath = Object.values(list).find((val) => val?.tab?.includes(path))
    if (isEmpty(currentPath?.menu)) {
      setCollapseDisabled(true)
      setSideBarCollapsed(true)
    } else {
      setCollapseDisabled(false)
      setSideBarCollapsed(sessionStorage.getItem('collaps') === 'true')
    }
  }, [pathname, employee?.id])

  useEffect(() => {
    initializeAppCalls()
  }, [])

  return (
    <>
      <PageLayout
        collapsed={sideBarCollapsed}
        sidePanel={
          <SideBarNav
            collapsed={sideBarCollapsed}
            logo={
              <img
                src={labels.navLogo}
                alt="log"
              />
            }
            menuData={list}
            activePath={pathname}
            onMenuClick={(nav: { menu: string[]; path: string }): void => {
              if (isEmpty(nav.menu)) {
                setSideBarCollapsed(true)
                history.push(nav.path)
                setCollapseDisabled(true)
              } else {
                handleCollapse()
              }
            }}
            onSubMenuClick={(item: { path: string }): void => {
              history.push(item.path)
            }}
          />
        }
        navigationHeader={
          <HeaderNavigation
            employee={{
              name: employee?.full_name_i18n,
              avatar: employee?.avatar_thumb,
              email: employee?.email,
            }}
            showAvatarCompanyIcon={false}
            notificationButtonClick={(): void => {
              dispatch(toggleNotificationsPanel(true))
            }}
            onClickListItem={handleHeaderNavigation}
            dropdownListItems={listItems}
            toggleSidebar={toggleSidebar}
            collapsed={sideBarCollapsed}
            collapseDisabled={collapseDisabled}
            headerCheldren={
              <>
                {canPostAnnouncement && <Announcement />}
                <Button
                  size="sm"
                  variant="tertiaryGray"
                  iconLeading={<Icon name="bell-02" />}
                  iconOnly
                  onClick={(): void => {
                    dispatch(toggleNotificationsPanel(true))
                  }}
                />
                {labels.name === 'Jisr' && <HelpCenter />}
              </>
            }
          />
        }
        body={
          <>
            {children}
            <UserSnap />
          </>
        }
      />
      <ChangePasswordModal
        isOpen={isPasswordModalOpen}
        onClose={(): void => setIsPasswordModalOpen(false)}
      />
    </>
  )
}

export default NewNavigation
